import React, { useEffect, useRef, useState } from "react";
import { BrowserRouter as Router, Routes, Route, useLocation } from "react-router-dom";
import { useGlobalShortcuts } from "./hooks/useGlobalShortcuts";
import CommandPalette from "./components/common/CommandPalette";
import ShortcutsModal from "./components/common/ShortcutsModal";
// Inner component that lives inside <Router> so useNavigate / useLocation work
import { Toaster, toast } from "react-hot-toast";
import Layout from "./components/layout/Layout";
import Dashboard from "./components/modules/DashboardNew";
import AttendanceTracking from "./components/modules/AttendanceTracking";
import AttendanceDashboard from "./components/modules/AttendanceDashboard";
import EmployeeMobileDashboard from "./components/modules/EmployeeMobileDashboard";
// @ts-ignore
import EmployeeCabinet from "./components/modules/EmployeeCabinet";
import CRMLeadsList from "./components/modules/CRMLeadsList";
// @ts-ignore
import PhoneLauncher from "./components/modules/PhoneLauncher";
import WhatsAppDashboard from "./components/modules/WhatsAppDashboard";
import LiveDataMock from "./components/modules/LiveDataMock";
import ShopifyProfitReport from "./components/modules/ShopifyProfitReport";
import ComingSoon from "./components/common/ComingSoon";
import LoginPage from "./components/auth/LoginPage";
// @ts-ignore
import MarketIntelligence from "./components/modules/MarketIntelligence";
// @ts-ignore
import InventoryDashboard from "./components/modules/InventoryDashboard";
// @ts-ignore
import VoiceSalesDashboard from "./components/modules/VoiceSalesDashboard";
// @ts-ignore
import SEODashboard from "./components/modules/SEODashboard";
import MarketplaceDashboard from "./components/modules/MarketplaceDashboard";
import PricingDashboard from "./components/modules/PricingDashboard";
// @ts-ignore
import AccountingDashboard from "./components/modules/AccountingDashboard";
import HRDashboard from "./components/modules/HRDashboard";
import AICommandCenter from "./components/modules/AICommandCenter";
import VeridianGinie from "./components/modules/VeridianGinie";
import OmniVoiceDashboard from "./components/modules/OmniVoiceDashboard";
import SmartPhoneBridge from "./components/modules/SmartPhoneBridge";
import AdminPanel from "./components/modules/AdminPanel";
import UserCabinet from "./components/modules/UserCabinet";
import IntegrationsHub from "./components/modules/IntegrationsHub";
import AfaqLeadDashboard from "./components/modules/AfaqLeadDashboard";
import CommissionDashboard from "./components/modules/CommissionDashboard";
import ContentAutomation from "./components/modules/ContentAutomation";
import AgentsHub from "./components/modules/AgentsHub";
import EmailIntelligence from "./components/modules/EmailIntelligence";
import VeridianCrawlerDashboard from "./components/modules/VeridianCrawlerDashboard";
import HelpDashboard from "./components/modules/HelpDashboard";
import HarmonyDashboard from "./components/modules/HarmonyDashboard";
import B2BTiersDashboard from "./components/modules/B2BTiersDashboard";
import HelpFab from "./components/common/HelpFab";
import { NotificationProvider } from "./context/NotificationContext";

interface User {
  name: string;
  role: string;
  phone: string;
}

const ROLE_WELCOME: Record<string, { greeting: string; emoji: string }> = {
  super_admin:      { greeting: "Welcome back, Commander",  emoji: "âš¡" },
  owner:            { greeting: "Welcome back",              emoji: "ðŸ‘‘" },
  general_manager:  { greeting: "Good to see you",          emoji: "ðŸ›ï¸" },
  tech_shareholder: { greeting: "Welcome back",             emoji: "âš™ï¸" },
  shareholder:      { greeting: "Welcome back",             emoji: "ðŸ’¼" },
  consultant:       { greeting: "Welcome back",             emoji: "ðŸ¤" },
  accountant:       { greeting: "Welcome back",             emoji: "ðŸ“Š" },
  labor:            { greeting: "Welcome back",             emoji: "ðŸ”§" },
  employee:         { greeting: "Welcome back",             emoji: "ðŸ‘¤" },
};

// Inner component that lives inside <Router> so useNavigate / useLocation work
const GlobalShortcutLayer: React.FC = () => {
  const location = useLocation();
  const [cmdOpen, setCmdOpen]             = useState(false);
  const [shortcutsOpen, setShortcutsOpen] = useState(false);

  useGlobalShortcuts({
    onOpenCommandPalette: () => setCmdOpen(true),
    onOpenShortcutsModal: () => setShortcutsOpen(true),
    onOpenTrainingGuide:  () => {},  // each module handles its own guide via ? key
  });

  // Close modals on route change
  useEffect(() => { setCmdOpen(false); setShortcutsOpen(false); }, [location.pathname]);

  return (
    <>
      <CommandPalette isOpen={cmdOpen} onClose={() => setCmdOpen(false)} />
      <ShortcutsModal isOpen={shortcutsOpen} onClose={() => setShortcutsOpen(false)} />
    </>
  );
};

const App: React.FC = () => {
  const [user, setUser]         = useState<User | null>(null);
  const [checking, setChecking] = useState(true);
  const welcomeShown            = useRef(false);

  // Detect mobile device â€” phone UA or narrow screen
  const isMobileDevice = useRef(
    /iphone|android|mobile|blackberry|windows phone/i.test(navigator.userAgent) ||
    window.innerWidth <= 768
  );

  // Check existing session on mount
  useEffect(() => {
    fetch("/api/auth/me", { credentials: "include" })
      .then(r => r.json())
      .then(data => {
        if (data.authenticated) {
          setUser({ name: data.name, role: data.role, phone: data.phone || "" });
        }
      })
      .catch(() => {})
      .finally(() => setChecking(false));
  }, []);

  // Show welcome toast once on login
  const handleLogin = (u: User) => {
    setUser(u);
    welcomeShown.current = false; // allow toast on fresh login
  };

  useEffect(() => {
    if (!user || welcomeShown.current) return;
    welcomeShown.current = true;
    const rw = ROLE_WELCOME[user.role] || ROLE_WELCOME.employee;
    const firstName = user.name.split(" ")[0];
    toast(`${rw.emoji} ${rw.greeting}, ${firstName}!`, {
      duration: 4000,
      style: {
        background: "rgba(19,27,46,0.95)",
        color: "#ffd6a5",
        border: "1px solid rgba(255,214,165,0.2)",
        fontFamily: "'Manrope', sans-serif",
        fontWeight: 700,
        backdropFilter: "blur(16px)",
        fontSize: 14,
        padding: "12px 20px",
      },
    });
  }, [user]);

  const handleLogout = async () => {
    await fetch("/api/auth/logout", { method: "POST", credentials: "include" });
    toast("Signed out", {
      style: { background: "#171f33", color: "#9b8f83", fontFamily: "'Space Grotesk', sans-serif" }
    });
    setUser(null);
    welcomeShown.current = false;
  };

  // Splash while checking session
  if (checking) {
    return (
      <div style={{
        minHeight: "100vh", background: "#0b1326",
        display: "flex", alignItems: "center", justifyContent: "center",
      }}>
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
        <div style={{
          width: 52, height: 52, borderRadius: "50%",
          background: "linear-gradient(135deg, #ffd6a5, #e7c090)",
          display: "flex", alignItems: "center", justifyContent: "center",
          boxShadow: "0 0 40px rgba(255,214,165,0.3)",
          animation: "spin 1.2s linear infinite",
          fontSize: 24,
        }}>
          ðŸ”
        </div>
      </div>
    );
  }

  // Not authenticated â€” login page is the only thing shown
  if (!user) {
    return (
      <>
        <Toaster position="top-center" />
        <LoginPage onLogin={handleLogin} />
      </>
    );
  }

  // Employee on mobile â†’ full-screen mobile dashboard, no sidebar
  if (user && isMobileDevice.current && user.role === "employee") {
    return (
      <>
        <Toaster position="top-center" />
        <EmployeeMobileDashboard employeeName={user.name} />
      </>
    );
  }

  // Authenticated â€” full app, login page is never rendered again
  return (
    <NotificationProvider>
      <Router>
        <GlobalShortcutLayer />
        <Toaster position="top-right" />
        <Layout user={user} onLogout={handleLogout}>
          <Routes>
            <Route path="/"                  element={<Dashboard />} />
            <Route path="/my-dashboard"      element={<EmployeeMobileDashboard employeeName={user.name} />} />
            <Route path="/attendance"        element={<AttendanceTracking user={user} />} />
            <Route path="/attendance/dashboard" element={<AttendanceDashboard />} />
            <Route path="/employees/:id"      element={<EmployeeCabinet />} />
            <Route path="/employees/*"       element={<AttendanceTracking user={user} />} />
            <Route path="/crm/phone"          element={<PhoneLauncher />} />
            <Route path="/crm/*"             element={<CRMLeadsList />} />
            <Route path="/live-data/*"       element={<LiveDataMock />} />
            <Route path="/whatsapp/*"        element={<WhatsAppDashboard />} />
            <Route path="/inventory/*"       element={<InventoryDashboard />} />
            <Route path="/voice-sales/*"     element={<VoiceSalesDashboard />} />
            <Route path="/seo/*"             element={<SEODashboard />} />
            <Route path="/pricing/*"         element={<PricingDashboard />} />
            <Route path="/marketplace/*"  element={<MarketplaceDashboard />} />
            <Route path="/market-intel/*"    element={<MarketIntelligence />} />
            <Route path="/automation"        element={<AgentsHub />} />
            <Route path="/automation/*"      element={<AgentsHub />} />
            <Route path="/content"           element={<ContentAutomation />} />
            <Route path="/content/*"         element={<ContentAutomation />} />
            <Route path="/agents"            element={<AgentsHub />} />
            <Route path="/agents/*"          element={<AgentsHub />} />
            <Route path="/email"             element={<EmailIntelligence />} />
            <Route path="/email/*"           element={<EmailIntelligence />} />
            <Route path="/reports/*"         element={<ComingSoon module="Reports" />} />
            <Route path="/shopify"           element={<ShopifyProfitReport />} />
            <Route path="/profit"            element={<ShopifyProfitReport />} />
            <Route path="/accounting/*"      element={<AccountingDashboard />} />
            <Route path="/hr/*"              element={<HRDashboard />} />
            <Route path="/ai/*"              element={<AICommandCenter />} />
            <Route path="/admin/*"           element={<AdminPanel />} />
            <Route path="/profile/*"         element={<UserCabinet />} />
            <Route path="/integrations/*"    element={<IntegrationsHub />} />
            <Route path="/phone-bridge/*"    element={<SmartPhoneBridge />} />
            <Route path="/leads/*"           element={<AfaqLeadDashboard />} />
            <Route path="/commission/*"      element={<CommissionDashboard />} />
            <Route path="/veridian/*"        element={<VeridianGinie />} />
            <Route path="/omnivoice/*"       element={<OmniVoiceDashboard />} />
            <Route path="/crawler/*"         element={<VeridianCrawlerDashboard />} />
            <Route path="/help"              element={<HelpDashboard />} />
            <Route path="/help/*"            element={<HelpDashboard />} />
            <Route path="/harmony"           element={<HarmonyDashboard />} />
            <Route path="/b2b-tiers/*"       element={<B2BTiersDashboard />} />
          </Routes>
          <HelpFab />
        </Layout>
      </Router>
    </NotificationProvider>
  );
};

export default App;


